import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  transfersUrl, transferByIdUrl, transferApproveUrl, transferDispatchUrl,
  transferReceiveUrl, transferRejectUrl, transferCancelUrl,
  uploadTransferDocUrl, branchesUrl, inventoryUrl, ASSETS,
} from '../../BaseURL';
import Modal from '../../components/Modal';
import { fmtDate, fmtNumber, titleCase } from '../../utils/format';
import { toastSuccess, showAxiosError, swalConfirm, swalDangerConfirm } from '../../utils/swal';

const REASON_LABEL = {
  match:        'Received in full',
  short:        'Short — some missing',
  damaged:      'Damaged in transit',
  not_received: 'Not received at all',
};

// ─── status → badge map ────────────────────────────────────────────────
const STATUS_BADGE = {
  pending:                 'badge-gold',
  approved:                'badge-blue',
  dispatched:              'badge-blue',
  received:                'badge-green',
  received_with_variance:  'badge-gold',
  rejected:                'badge-red',
  cancelled:               'badge-gray',
};

const Transfers = () => {
  const [me] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } });
  const isSuper   = me?.role === 'super_admin';
  const isManager = me?.role === 'branch_manager' || isSuper;
  const myBranchId = me?.branch_id;

  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', direction: 'all', search: '', refBranch: '' });

  // Create
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ dest_branch_id: '', note: '', invoice_image: '' });
  const [lines, setLines] = useState([]);
  const [sourceInv, setSourceInv] = useState([]);   // products at the chosen source
  const [loadingInv, setLoadingInv] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [createErr, setCreateErr] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const invoiceInputRef = useRef(null);
  const cameraInputRef  = useRef(null);

  // Zoomed-image lightbox
  const [zoomImage, setZoomImage] = useState(null);

  // View / detail
  const [view, setView] = useState(null);           // { transfer, items }

  // Receive
  const [receive, setReceive] = useState(null);     // { transfer, items, received: { [id]: qty }, note }

  // Reject
  const [reject, setReject] = useState(null);       // { transfer, reason }

  // ─── data loaders ────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.direction && filter.direction !== 'all') params.direction = filter.direction;
      // For super admin, include the reference branch so Incoming/Outgoing
      // has something to filter against. Branch users are always self-scoped.
      if (isSuper && filter.direction !== 'all' && filter.refBranch) {
        params.branch_id = filter.refBranch;
      }
      // Branches are needed by everyone: super admins use them for the
      // Incoming/Outgoing filter, branch managers need them to populate the
      // destination dropdown in the "New Stock Transfer" modal.
      const [t, b] = await Promise.all([
        axios.get(transfersUrl, { params }),
        axios.get(branchesUrl).catch(() => ({ data: { results: [] } })),
      ]);
      setRows(t.data?.results || []);
      setBranches(b.data?.results || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [filter.status, filter.direction, filter.refBranch]);

  // Source is always the caller's own branch — you can only ship stock FROM
  // where you actually are. Fetch that branch's inventory when the create
  // modal opens.
  useEffect(() => {
    if (!openCreate || !myBranchId) { setSourceInv([]); return; }
    setLoadingInv(true);
    axios.get(inventoryUrl, { params: { branch_id: myBranchId } })
      .then((res) => setSourceInv(res.data?.results || []))
      .catch(() => setSourceInv([]))
      .finally(() => setLoadingInv(false));
  }, [openCreate, myBranchId]);

  const inventoryByProduct = useMemo(
    () => new Map(sourceInv.map((p) => [p.product_id, p])),
    [sourceInv]
  );

  // ─── derived stats ───────────────────────────────────────────────────
  const stats = useMemo(() => {
    let pendingOut = 0, pendingIn = 0, inTransit = 0, withVariance = 0;
    for (const r of rows) {
      const iAmSource = r.source_branch_id === myBranchId;
      const iAmDest   = r.dest_branch_id   === myBranchId;
      if (r.status === 'pending' || r.status === 'approved') {
        if (isSuper || iAmSource) pendingOut += 1;
        if (iAmDest) pendingIn += 1;
      }
      if (r.status === 'dispatched') {
        inTransit += 1;
        if (iAmDest) pendingIn += 1;
      }
      if (r.has_variance) withVariance += 1;
    }
    return { pendingOut, pendingIn, inTransit, withVariance };
  }, [rows, myBranchId, isSuper]);

  const filtered = useMemo(() => {
    const q = filter.search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.transfer_no || '').toLowerCase().includes(q)
      || (r.source_branch_name || '').toLowerCase().includes(q)
      || (r.dest_branch_name || '').toLowerCase().includes(q)
      || (r.requested_by_name || '').toLowerCase().includes(q)
    );
  }, [rows, filter.search]);

  // Dispatched transfers awaiting THIS user's receipt.
  // Branch manager: shown for their branch. Super admin: shown across all branches.
  const awaiting = useMemo(() => rows.filter((r) => {
    if (r.status !== 'dispatched') return false;
    if (isSuper) return true;
    return me?.role === 'branch_manager' && r.dest_branch_id === myBranchId;
  }), [rows, isSuper, me, myBranchId]);

  // ─── create flow ─────────────────────────────────────────────────────
  const openNew = () => {
    setForm({ dest_branch_id: '', note: '', invoice_image: '' });
    setLines([]);
    setProductSearch('');
    setCreateErr(null);
    setOpenCreate(true);
  };

  const uploadInvoice = async (file) => {
    if (!file) return;
    setUploadingInvoice(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(uploadTransferDocUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.success) {
        setForm((f) => ({ ...f, invoice_image: res.data.path }));
        toastSuccess('Invoice attached');
      } else {
        setCreateErr(res.data?.message || 'Upload failed');
      }
    } catch (err) {
      setCreateErr(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingInvoice(false);
      if (invoiceInputRef.current) invoiceInputRef.current.value = '';
      if (cameraInputRef.current)  cameraInputRef.current.value  = '';
    }
  };

  const removeLine = (idx) => setLines((l) => l.filter((_, i) => i !== idx));
  const updateLine = (idx, patch) => setLines((l) => l.map((ln, i) => i === idx ? { ...ln, ...patch } : ln));

  // Product picker — search + add helpers
  const linesByProduct = useMemo(
    () => new Set(lines.map((l) => Number(l.product_id))),
    [lines]
  );

  // Products with any available stock at this branch (base list, always shown)
  const availableSourceItems = useMemo(
    () => sourceInv.filter((p) => Number(p.qty_available ?? p.qty_on_hand ?? 0) > 0),
    [sourceInv]
  );

  // Filtered by the search box on top (if empty, show the full list)
  const pickerItems = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return availableSourceItems;
    return availableSourceItems.filter((p) =>
      (p.name || '').toLowerCase().includes(q)
      || (p.sku || '').toLowerCase().includes(q)
      || (p.barcode || '').toLowerCase().includes(q)
    );
  }, [availableSourceItems, productSearch]);

  const addProductLine = (productId) => {
    const pid = Number(productId);
    if (linesByProduct.has(pid)) return;
    setLines((prev) => [...prev, { product_id: String(pid), qty: '' }]);
    setProductSearch('');
  };
  const totalUnits = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.qty) || 0), 0),
    [lines]
  );

  const submitCreate = async () => {
    setCreateErr(null);
    if (!myBranchId) return setCreateErr('You must be assigned to a branch before creating transfers. Ask your super administrator.');
    if (!form.dest_branch_id)   return setCreateErr('Choose a destination branch');
    if (Number(myBranchId) === Number(form.dest_branch_id)) return setCreateErr('Destination cannot be the same as your branch');
    const items = lines
      .filter((l) => l.product_id && Number(l.qty) > 0)
      .map((l) => ({ product_id: Number(l.product_id), qty: Number(l.qty) }));
    if (items.length === 0) return setCreateErr('Add at least one product with qty > 0');

    // Local availability sanity check
    for (const it of items) {
      const inv = inventoryByProduct.get(it.product_id);
      const avail = Number(inv?.qty_available ?? inv?.qty_on_hand ?? 0);
      if (avail < it.qty) {
        return setCreateErr(`"${inv?.name || `Product #${it.product_id}`}" — only ${avail} available at source`);
      }
    }

    setSaving(true);
    try {
      const res = await axios.post(transfersUrl, {
        source_branch_id: Number(myBranchId),
        dest_branch_id: Number(form.dest_branch_id),
        items, note: form.note || null,
        invoice_image: form.invoice_image || null,
      });
      if (res.data?.success) {
        setOpenCreate(false);
        toastSuccess(`Transfer ${res.data.transferNo} drafted`);
        await load();
      } else setCreateErr(res.data?.message || 'Failed to create');
    } catch (err) {
      setCreateErr(err.response?.data?.message || 'Failed to create');
    } finally { setSaving(false); }
  };

  // ─── detail / actions ────────────────────────────────────────────────
  const openView = async (id) => {
    try {
      const res = await axios.get(transferByIdUrl(id));
      if (res.data?.success) setView({ transfer: res.data.transfer, items: res.data.items });
    } catch (err) { showAxiosError(err); }
  };

  const doSimpleAction = async (url, successMsg, confirmTitle, confirmText, danger = false) => {
    const confirmed = danger
      ? await swalDangerConfirm(confirmTitle, confirmText, { confirmButtonText: 'Yes, continue' })
      : await swalConfirm(confirmTitle, confirmText, { confirmButtonText: 'Yes, continue' });
    if (!confirmed) return;
    try {
      await axios.patch(url);
      toastSuccess(successMsg);
      setView(null);
      await load();
    } catch (err) { showAxiosError(err); }
  };

  // ─── receive flow ────────────────────────────────────────────────────
  // Line state: { qty: string, reason: 'match'|'short'|'damaged'|'not_received' }
  const openReceive = (vw) => {
    const lines = {};
    for (const it of vw.items) lines[it.id] = { qty: String(it.qty), reason: 'match' };
    setReceive({ transfer: vw.transfer, items: vw.items, lines, note: '' });
  };

  // Auto-adjust the reason when the qty changes (so managers don't have to click twice)
  const setReceiveQty = (lineId, sentQty, nextQty) => {
    setReceive((prev) => {
      if (!prev) return prev;
      const q = nextQty;
      const nq = Number(q);
      let reason = prev.lines[lineId]?.reason || 'match';
      if (nq === Number(sentQty))      reason = 'match';
      else if (nq === 0)               reason = 'not_received';
      else if (reason === 'match')     reason = 'short';   // was matching, now isn't — default to "short"
      return { ...prev, lines: { ...prev.lines, [lineId]: { qty: q, reason } } };
    });
  };
  const setReceiveReason = (lineId, reason) => {
    setReceive((prev) => prev && ({ ...prev, lines: { ...prev.lines, [lineId]: { ...prev.lines[lineId], reason } } }));
  };

  const submitReceive = async () => {
    if (!receive) return;
    const payload = receive.items.map((it) => {
      const st = receive.lines[it.id] || { qty: it.qty, reason: 'match' };
      return { id: it.id, qty_received: Number(st.qty), reason: st.reason };
    });
    const anyMismatch = payload.some((l, i) => Number(receive.items[i].qty) !== Number(l.qty_received));
    if (anyMismatch) {
      const ok = await swalConfirm(
        'Confirm receive with variance',
        'Some lines differ from what was dispatched. The reasons you selected will be logged for audit. Proceed?',
        { confirmButtonText: 'Yes, receive' }
      );
      if (!ok) return;
    }
    try {
      await axios.patch(transferReceiveUrl(receive.transfer.id), { lines: payload, note: receive.note || null });
      toastSuccess(anyMismatch ? 'Received — variance logged' : 'Received and stock updated');
      setReceive(null);
      setView(null);
      await load();
    } catch (err) { showAxiosError(err); }
  };

  // ─── reject flow ─────────────────────────────────────────────────────
  const openReject = (vw) => setReject({ transfer: vw.transfer, reason: '' });
  const submitReject = async () => {
    if (!reject) return;
    if (!reject.reason.trim()) { showAxiosError(new Error('A reason is required when rejecting a transfer')); return; }
    try {
      await axios.patch(transferRejectUrl(reject.transfer.id), { reason: reject.reason });
      toastSuccess('Transfer rejected — stock returned to source');
      setReject(null);
      setView(null);
      await load();
    } catch (err) { showAxiosError(err); }
  };

  // ─── action eligibility per current user ─────────────────────────────
  const can = (t) => {
    if (!t) return {};
    const atSource = isSuper || (me?.role === 'branch_manager' && t.source_branch_id === myBranchId);
    const atDest   = isSuper || (me?.role === 'branch_manager' && t.dest_branch_id   === myBranchId);
    return {
      approve:  atSource && t.status === 'pending',
      dispatch: atSource && (t.status === 'pending' || t.status === 'approved'),
      cancel:   atSource && (t.status === 'pending' || t.status === 'approved'),
      receive:  atDest   &&  t.status === 'dispatched',
      reject:   atDest   &&  t.status === 'dispatched',
    };
  };

  // ─── rendering helpers ───────────────────────────────────────────────
  const StatusBadge = ({ status, variance }) => (
    <span className={`badge ${STATUS_BADGE[status] || 'badge-gray'}`}>
      {variance && status === 'received_with_variance' ? 'Received · variance' : titleCase(status || '')}
    </span>
  );

  return (
    <>
      {/* ─── Awaiting your receipt — spotlight for the dest manager ─── */}
      {awaiting.length > 0 && (
        <div className="xfer-inbox">
          <div className="xfer-inbox-head">
            <div className="xfer-inbox-title">
              <span className="ic">📥</span>
              <div>
                <h3>{isSuper ? 'Dispatched · awaiting receipt' : 'Awaiting your receipt'}</h3>
                <p>
                  {awaiting.length} transfer{awaiting.length === 1 ? '' : 's'} dispatched to
                  {isSuper ? ' one of the branches' : ' your branch'}. Open each to confirm what arrived, or flag missing / damaged items.
                </p>
              </div>
            </div>
            <span className="xfer-inbox-count">{awaiting.length}</span>
          </div>
          <div className="xfer-inbox-list">
            {awaiting.slice(0, 6).map((r) => (
              <button key={r.id} className="xfer-inbox-row" onClick={() => openView(r.id)}>
                <div className="col-1">
                  <div className="tno">{r.transfer_no}</div>
                  <div className="route">
                    From <strong>{r.source_branch_name || '—'}</strong>
                    {isSuper && <> → <strong>{r.dest_branch_name || '—'}</strong></>}
                  </div>
                </div>
                <div className="col-2">
                  <span className="chip">{r.line_count} lines</span>
                  <span className="chip">{fmtNumber(r.total_qty)} units</span>
                  <span className="chip mute">{fmtDate(r.dispatched_at || r.created_at)}</span>
                </div>
                <div className="col-3">
                  <span className="btn-primary btn-sm">Review & receive →</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── KPI strip ─── */}
      <div className="kpi-row">
        <KpiCard label="Pending Out"   val={stats.pendingOut}   hint="Drafts & approved" tone="brand"  icon="📤" />
        <KpiCard label="Pending In"    val={stats.pendingIn}    hint="Awaiting receipt"  tone="warn"   icon="📥" />
        <KpiCard label="In Transit"    val={stats.inTransit}    hint="Dispatched"        tone="accent" icon="🚚" />
        <KpiCard label="With Variance" val={stats.withVariance} hint="Discrepancies"     tone="mute"   icon="⚠️" />
      </div>

      {/* ─── Toolbar + Filters ─── */}
      <div className="panel">
        <div className="panel-head">
          <div className="head-text">
            <h2>Stock Transfers</h2>
            <span className="sub">Move inventory between branches with full chain-of-custody</span>
          </div>
          <div className="head-actions">
            {isManager && (
              <button className="btn-primary" onClick={openNew}>＋ New Transfer</button>
            )}
          </div>
        </div>

        <div className="toolbar">
          <div className="search-field" style={{ maxWidth: 320 }}>
            <span className="ic">🔍</span>
            <input
              className="f-input"
              placeholder="Search transfer #, branch, requester"
              value={filter.search}
              onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
            />
            {filter.search && (
              <button className="clear" onClick={() => setFilter((f) => ({ ...f, search: '' }))} aria-label="Clear">×</button>
            )}
          </div>

          <div className="segmented">
            {['all','in','out'].map((d) => (
              <button
                key={d}
                className={`seg ${filter.direction === d ? 'active' : ''}`}
                onClick={() => setFilter((f) => ({
                  ...f,
                  direction: d,
                  // When a super admin picks a direction for the first time,
                  // default the reference branch to HQ so results actually filter.
                  refBranch: (isSuper && d !== 'all' && !f.refBranch)
                    ? (branches.find((b) => b.is_head_office)?.id || branches[0]?.id || '')
                    : f.refBranch,
                }))}
              >
                {d === 'all' ? 'All' : d === 'in' ? '📥 Incoming' : '📤 Outgoing'}
              </button>
            ))}
          </div>

          {isSuper && filter.direction !== 'all' && (
            <div className="xfer-ref-picker" title="Direction is relative to this branch">
              <span className="lbl">Relative to</span>
              <select
                className="f-select"
                value={filter.refBranch}
                onChange={(e) => setFilter((f) => ({ ...f, refBranch: e.target.value }))}
              >
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}{b.is_head_office ? ' (HQ)' : ''}</option>
                ))}
              </select>
            </div>
          )}

          <select
            className="f-select"
            style={{ maxWidth: 200 }}
            value={filter.status}
            onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="dispatched">Dispatched</option>
            <option value="received">Received</option>
            <option value="received_with_variance">Received w/ variance</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {loading ? (
          <div className="empty-state"><div className="ic">⏳</div><h4>Loading transfers…</h4></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="ic">📦</div>
            <h4>No transfers yet</h4>
            <p>Create your first transfer to move stock between branches with a full audit trail.</p>
            {isManager && <button className="btn-primary" onClick={openNew}>＋ New Transfer</button>}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Dir</th>
                  <th>Transfer #</th>
                  <th>Route</th>
                  <th className="center">Lines</th>
                  <th className="right">Qty</th>
                  <th>Status</th>
                  <th>Requested</th>
                  <th>By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  // Direction relative to the current perspective:
                  //   • branch user → their own branch
                  //   • super admin → the picked reference branch (defaults to none)
                  const perspectiveBranch = isSuper
                    ? Number(filter.refBranch) || null
                    : Number(myBranchId) || null;
                  const isOutgoing = perspectiveBranch && Number(r.source_branch_id) === perspectiveBranch;
                  const isIncoming = perspectiveBranch && Number(r.dest_branch_id)   === perspectiveBranch;
                  const dirBadge = isOutgoing
                    ? { cls: 'xfer-dir-badge out', label: '📤 Out' }
                    : isIncoming
                      ? { cls: 'xfer-dir-badge in', label: '📥 In' }
                      : { cls: 'xfer-dir-badge neutral', label: '—' };
                  return (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => openView(r.id)}>
                      <td><span className={dirBadge.cls}>{dirBadge.label}</span></td>
                      <td className="strong">{r.transfer_no}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                          <span className={isOutgoing ? 'xfer-src-highlight' : ''}>{r.source_branch_name || '—'}</span>
                          <span className="muted" style={{ color: 'var(--mute-2)' }}>→</span>
                          <span className={isIncoming ? 'xfer-dst-highlight' : ''}>{r.dest_branch_name || '—'}</span>
                        </div>
                      </td>
                      <td className="center num">{r.line_count}</td>
                      <td className="right num">{fmtNumber(r.total_qty)}</td>
                      <td><StatusBadge status={r.status} variance={!!r.has_variance} /></td>
                      <td className="muted">{fmtDate(r.created_at)}</td>
                      <td>{r.requested_by_name || '—'}</td>
                      <td className="right">
                        <button className="btn-link" onClick={(e) => { e.stopPropagation(); openView(r.id); }}>View →</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Create Modal ─── */}
      <Modal
        title="New Stock Transfer"
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        width={1120}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setOpenCreate(false)}>Cancel</button>
            <button className="btn-primary" onClick={submitCreate} disabled={saving}>
              {saving ? 'Creating…' : 'Create Transfer'}
            </button>
          </>
        }
      >
        {createErr && <div className="notice">{createErr}</div>}

        {!myBranchId && (
          <div className="notice">
            You must be assigned to a branch to create transfers. Ask your super administrator to set your branch.
          </div>
        )}

        {/* Two-column layout — details on the left, product catalog on the right */}
        <div className="xfer-create-grid">
          <div className="xfer-create-left">
            {/* Route visualizer */}
            {myBranchId && (
              <div className="xfer-route">
                <div className="xfer-route-node">
                  <div className="node-label">From — your branch</div>
                  <div className="node-name">
                    {branches.find((b) => b.id === myBranchId)?.name || 'Your branch'}
                  </div>
                  <div className="node-hint">Stock is reserved on create, physically leaves on dispatch.</div>
                </div>

                <div className="xfer-route-arrow" aria-hidden>→</div>

                <div className="xfer-route-node dest">
                  <div className="node-label">To — destination <span className="req">*</span></div>
                  <select
                    className="f-select node-select"
                    value={form.dest_branch_id}
                    onChange={(e) => setForm({ ...form, dest_branch_id: e.target.value })}
                  >
                    <option value="">— Pick destination branch —</option>
                    {branches
                      .filter((b) => Number(b.id) !== Number(myBranchId))
                      .map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                  <div className="node-hint">Stock lands here after the receiver confirms.</div>
                </div>
              </div>
            )}

            {/* Added items */}
            {lines.length > 0 ? (
              <div className="xfer-lines">
                <div className="xfer-lines-head">
                  <span>Items to transfer</span>
                  <span className="ct">
                    {lines.length} product{lines.length === 1 ? '' : 's'} · {fmtNumber(totalUnits)} units
                  </span>
                </div>
                {lines.map((ln, idx) => {
                  const p = inventoryByProduct.get(Number(ln.product_id));
                  if (!p) return null;
                  const avail = Number(p.qty_available ?? p.qty_on_hand);
                  const overdrawn = Number(ln.qty) > avail;
                  return (
                    <div key={idx} className={`xfer-line ${overdrawn ? 'err' : ''}`}>
                      <div className="xfer-line-prod">
                        {p.image
                          ? <img src={`${ASSETS}${p.image}`} alt="" />
                          : <div className="ph">📦</div>}
                        <div className="prod-text">
                          <div className="name">{p.name}</div>
                          <div className="meta">
                            {p.sku && <>SKU {p.sku} · </>}
                            <strong>{fmtNumber(avail)}</strong> {p.unit || ''} available
                          </div>
                        </div>
                      </div>
                      <div className="xfer-line-qty">
                        <div className="input-group">
                          <input
                            type="number" min="0" step="any" className="f-input"
                            placeholder="Qty"
                            value={ln.qty}
                            onChange={(e) => updateLine(idx, { qty: e.target.value })}
                            style={{ border: 'none', boxShadow: 'none' }}
                            autoFocus={idx === lines.length - 1 && ln.qty === ''}
                          />
                          {p.unit && <span className="addon suffix">{p.unit}</span>}
                        </div>
                        {overdrawn && (
                          <span className="err-msg">Only {fmtNumber(avail)} {p.unit || ''} available</span>
                        )}
                      </div>
                      <button
                        className="icon-btn danger"
                        onClick={() => removeLine(idx)}
                        aria-label={`Remove ${p.name}`}
                      >×</button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="xfer-empty">
                <div className="ic">📦</div>
                <h4>No products added yet</h4>
                <p>Pick from your branch's stock on the right — each item you add is reserved so it can't be sold at POS while this transfer is pending.</p>
              </div>
            )}

            <div className="field" style={{ marginTop: 16 }}>
              <label className="f-label">Note (optional)</label>
              <textarea
                className="f-textarea"
                rows={2}
                placeholder="Anything the receiver should know…"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>

            {/* Invoice / dispatch note attachment */}
            <div className="field">
              <label className="f-label">Invoice / dispatch note (optional)</label>
              <span className="f-hint">
                Attach a photo of the invoice or dispatch note so the receiving branch can verify what arrived.
              </span>

              <input
                ref={invoiceInputRef} type="file" accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => uploadInvoice(e.target.files?.[0])}
              />
              <input
                ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                style={{ display: 'none' }}
                onChange={(e) => uploadInvoice(e.target.files?.[0])}
              />

              {form.invoice_image ? (
                <div className="invoice-preview">
                  <img src={`${ASSETS}${form.invoice_image}`} alt="Invoice preview" onClick={() => setZoomImage(form.invoice_image)} />
                  <div className="invoice-preview-actions">
                    <button className="btn-ghost btn-sm" onClick={() => invoiceInputRef.current?.click()} disabled={uploadingInvoice}>Replace</button>
                    <button className="btn-danger btn-sm" onClick={() => setForm({ ...form, invoice_image: '' })}>Remove</button>
                  </div>
                </div>
              ) : (
                <div className="invoice-drop">
                  <div className="ic">🧾</div>
                  <div className="msg">
                    <strong>Attach an invoice</strong>
                    <div className="hint">JPG or PNG, up to 8&nbsp;MB</div>
                  </div>
                  <div className="invoice-drop-actions">
                    <button
                      type="button" className="btn-ghost btn-sm"
                      onClick={() => invoiceInputRef.current?.click()}
                      disabled={uploadingInvoice}
                    >📎 Choose file</button>
                    <button
                      type="button" className="btn-primary btn-sm"
                      onClick={() => cameraInputRef.current?.click()}
                      disabled={uploadingInvoice}
                    >📷 Take photo</button>
                  </div>
                </div>
              )}
              {uploadingInvoice && <span className="f-hint">Uploading…</span>}
            </div>
          </div>

          {/* ─── Right column: sticky product catalog ─── */}
          <aside className="xfer-create-right">
            <div className="xfer-create-right-head">
              <div>
                <div className="rc-title">Available stock</div>
                <div className="rc-sub">
                  at {branches.find((b) => b.id === myBranchId)?.name || 'your branch'}
                </div>
              </div>
              <span className="rc-count">
                {loadingInv ? '…' : availableSourceItems.length}
              </span>
            </div>

            <div className="search-field rc-search">
              <span className="ic">🔍</span>
              <input
                className="f-input"
                placeholder="Search by name, SKU or barcode…"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                disabled={loadingInv || !myBranchId}
              />
              {productSearch && (
                <button className="clear" onClick={() => setProductSearch('')} aria-label="Clear">×</button>
              )}
            </div>

            <div className="xfer-picker-list rc-list">
              {loadingInv ? (
                <div className="xfer-picker-empty">Loading your branch's stock…</div>
              ) : availableSourceItems.length === 0 ? (
                <div className="xfer-picker-empty">
                  <strong>No sellable stock at your branch.</strong>
                  <div style={{ marginTop: 4, fontSize: 12 }}>
                    Receive a purchase or an incoming transfer first, then come back to send stock elsewhere.
                  </div>
                </div>
              ) : pickerItems.length === 0 ? (
                <div className="xfer-picker-empty">
                  No products match “{productSearch}”.
                </div>
              ) : (
                pickerItems.map((p) => {
                  const added = linesByProduct.has(p.product_id);
                  const avail = Number(p.qty_available ?? p.qty_on_hand);
                  return (
                    <button
                      key={p.product_id}
                      type="button"
                      className={`xfer-picker-item ${added ? 'added' : ''}`}
                      onClick={() => addProductLine(p.product_id)}
                      disabled={added}
                    >
                      <div className="prod">
                        {p.image
                          ? <img src={`${ASSETS}${p.image}`} alt="" />
                          : <div className="ph">📦</div>}
                        <div>
                          <div className="name">{p.name}</div>
                          <div className="meta">
                            {p.sku && <>SKU {p.sku} · </>}
                            <strong>{fmtNumber(avail)}</strong> {p.unit || ''} available
                            {Number(p.qty_reserved) > 0 && (
                              <> · <span className="muted-inline">{fmtNumber(p.qty_reserved)} reserved</span></>
                            )}
                          </div>
                        </div>
                      </div>
                      <span className="action">{added ? '✓ Added' : '＋ Add'}</span>
                    </button>
                  );
                })
              )}
            </div>
          </aside>
        </div>
      </Modal>

      {/* ─── Detail Modal ─── */}
      <Modal
        title={view ? `Transfer ${view.transfer.transfer_no}` : ''}
        open={!!view}
        onClose={() => setView(null)}
        width={780}
        footer={
          view && (() => {
            const c = can(view.transfer);
            return (
              <>
                <button className="btn-ghost" onClick={() => setView(null)}>Close</button>
                {c.cancel && (
                  <button className="btn-danger" onClick={() => doSimpleAction(
                    transferCancelUrl(view.transfer.id),
                    'Transfer cancelled',
                    'Cancel this transfer?',
                    'No stock has moved yet. The transfer will be voided.',
                    true,
                  )}>Cancel transfer</button>
                )}
                {c.reject && (
                  <button className="btn-danger" onClick={() => openReject(view)}>Reject</button>
                )}
                {c.approve && (
                  <button className="btn-ghost" onClick={() => doSimpleAction(
                    transferApproveUrl(view.transfer.id),
                    'Transfer approved',
                    'Approve this transfer?',
                    'It will become eligible for dispatch.',
                  )}>Approve</button>
                )}
                {c.dispatch && (
                  <button className="btn-gold" onClick={() => doSimpleAction(
                    transferDispatchUrl(view.transfer.id),
                    'Transfer dispatched',
                    'Dispatch this transfer?',
                    'Stock will be removed from the source branch immediately.',
                  )}>📤 Dispatch</button>
                )}
                {c.receive && (
                  <button className="btn-primary" onClick={() => openReceive(view)}>📥 Receive</button>
                )}
              </>
            );
          })()
        }
      >
        {view && (
          <>
            <div className="field-row" style={{ marginBottom: 18 }}>
              <Detail label="Route" value={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="strong">{view.transfer.source_branch_name}</span>
                  <span style={{ color: 'var(--mute-2)' }}>→</span>
                  <span className="strong">{view.transfer.dest_branch_name}</span>
                </div>
              } />
              <Detail label="Status" value={<StatusBadge status={view.transfer.status} variance={!!view.transfer.has_variance} />} />
            </div>

            <Timeline t={view.transfer} />

            <div className="divider-text">Items</div>

            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="right">Dispatched</th>
                    <th className="right">Received</th>
                    <th className="right">Variance</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {view.items.map((it) => {
                    const rec = it.qty_received == null ? null : Number(it.qty_received);
                    const varQty = rec == null ? null : (Number(it.qty) - rec);
                    return (
                      <tr key={it.id}>
                        <td>
                          <div className="strong">{it.product_name}</div>
                          <div className="muted" style={{ fontSize: 12 }}>{it.sku ? `SKU ${it.sku}` : ''}{it.unit ? ` · ${it.unit}` : ''}</div>
                        </td>
                        <td className="right num">{fmtNumber(it.qty)}</td>
                        <td className="right num">{rec == null ? '—' : fmtNumber(rec)}</td>
                        <td className="right num">
                          {varQty == null ? '—' : varQty === 0
                            ? <span className="badge badge-green no-dot">match</span>
                            : <span className="badge badge-red no-dot">-{fmtNumber(varQty)}</span>}
                        </td>
                        <td>{it.reason ? <span className="muted" style={{ fontSize: 12 }}>{REASON_LABEL[it.reason] || titleCase(it.reason)}</span> : <span className="muted">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {view.transfer.invoice_image && (
              <>
                <div className="divider-text">Invoice / dispatch note</div>
                <div className="invoice-viewer">
                  <img
                    src={`${ASSETS}${view.transfer.invoice_image}`}
                    alt="Invoice"
                    onClick={() => setZoomImage(view.transfer.invoice_image)}
                  />
                  <div className="hint">Click the image to enlarge.</div>
                </div>
              </>
            )}

            {view.transfer.note && (
              <div className="notice info" style={{ marginTop: 14 }}>
                <strong>Note:</strong>&nbsp;{view.transfer.note}
              </div>
            )}
            {view.transfer.rejection_reason && (
              <div className="notice" style={{ marginTop: 14 }}>
                <strong>Rejection reason:</strong>&nbsp;{view.transfer.rejection_reason}
              </div>
            )}
          </>
        )}
      </Modal>

      {/* ─── Receive Modal ─── */}
      <Modal
        title={receive ? `Receive ${receive.transfer.transfer_no}` : ''}
        open={!!receive}
        onClose={() => setReceive(null)}
        width={720}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setReceive(null)}>Cancel</button>
            <button className="btn-primary" onClick={submitReceive}>Confirm receive</button>
          </>
        }
      >
        {receive && (
          <>
            <div className="notice info">
              Confirm what you actually received for each line. Leave unchanged if it matches what was sent.
              When a line is short, damaged, or not received, pick a reason so the source branch knows why.
            </div>

            {receive.transfer.invoice_image && (
              <div className="invoice-viewer" style={{ marginBottom: 14 }}>
                <img
                  src={`${ASSETS}${receive.transfer.invoice_image}`}
                  alt="Invoice"
                  onClick={() => setZoomImage(receive.transfer.invoice_image)}
                />
                <div className="hint">Invoice from source — compare against physical delivery.</div>
              </div>
            )}

            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="right">Sent</th>
                    <th className="right">Received</th>
                    <th>Status / Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {receive.items.map((it) => {
                    const st = receive.lines[it.id] || { qty: it.qty, reason: 'match' };
                    const nq = Number(st.qty);
                    const mismatch = nq !== Number(it.qty);
                    return (
                      <tr key={it.id}>
                        <td>
                          <div className="strong">{it.product_name}</div>
                          <div className="muted" style={{ fontSize: 12 }}>{it.sku ? `SKU ${it.sku}` : ''}{it.unit ? ` · ${it.unit}` : ''}</div>
                        </td>
                        <td className="right num">{fmtNumber(it.qty)}</td>
                        <td className="right" style={{ width: 140 }}>
                          <input
                            type="number" min="0" max={Number(it.qty)} step="any"
                            className="f-input"
                            style={{ textAlign: 'right' }}
                            value={st.qty}
                            onChange={(e) => setReceiveQty(it.id, it.qty, e.target.value)}
                          />
                        </td>
                        <td style={{ width: 220 }}>
                          {!mismatch ? (
                            <span className="badge badge-green no-dot">Received in full</span>
                          ) : (
                            <select
                              className="f-select"
                              value={st.reason === 'match' ? 'short' : st.reason}
                              onChange={(e) => setReceiveReason(it.id, e.target.value)}
                              style={{ minHeight: 36, padding: '6px 10px', fontSize: 13 }}
                            >
                              <option value="short">Short — some missing</option>
                              <option value="damaged">Damaged in transit</option>
                              <option value="not_received">Not received at all</option>
                            </select>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="field" style={{ marginTop: 14 }}>
              <label className="f-label">Overall note (optional)</label>
              <textarea
                className="f-textarea" rows={2}
                placeholder="Anything else the source branch should know about this delivery…"
                value={receive.note}
                onChange={(e) => setReceive({ ...receive, note: e.target.value })}
              />
            </div>
          </>
        )}
      </Modal>

      {/* ─── Reject Modal ─── */}
      <Modal
        title={reject ? `Reject ${reject.transfer.transfer_no}` : ''}
        open={!!reject}
        onClose={() => setReject(null)}
        width={520}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setReject(null)}>Cancel</button>
            <button className="btn-danger" onClick={submitReject}>Reject & return stock</button>
          </>
        }
      >
        {reject && (
          <>
            <div className="notice">
              Rejecting will return <strong>all dispatched stock</strong> to the source branch. The transfer
              will be closed and the reason logged for audit.
            </div>
            <div className="field">
              <label className="f-label">Reason <span className="req">*</span></label>
              <textarea
                className="f-textarea" rows={3} autoFocus
                placeholder="e.g. Wrong products / damaged goods / not expecting this delivery"
                value={reject.reason}
                onChange={(e) => setReject({ ...reject, reason: e.target.value })}
              />
            </div>
          </>
        )}
      </Modal>

      {/* ─── Invoice image lightbox ─── */}
      {zoomImage && (
        <div className="img-lightbox" onClick={() => setZoomImage(null)}>
          <img src={`${ASSETS}${zoomImage}`} alt="Invoice full size" />
          <button className="img-lightbox-close" onClick={() => setZoomImage(null)} aria-label="Close">×</button>
        </div>
      )}
    </>
  );
};

// ─── Small presentational helpers ──────────────────────────────────────
const KpiCard = ({ label, val, hint, tone, icon }) => (
  <div className={`stat`}>
    <div className="lbl">{label}</div>
    <div className="val">{fmtNumber(val)}</div>
    <div className="hint">{hint}</div>
    <div className="ic" style={tone === 'brand'  ? { background: 'var(--brand-soft)',   color: 'var(--brand)' }
                       : tone === 'accent' ? { background: 'var(--accent-soft)',  color: 'var(--accent)' }
                       : tone === 'warn'   ? { background: 'var(--warn-soft)',    color: 'var(--warn)' }
                       : tone === 'mute'   ? { background: 'var(--bg-tint)',      color: 'var(--mute)' }
                       : {}}>{icon}</div>
  </div>
);

const Detail = ({ label, value }) => (
  <div className="field" style={{ marginBottom: 0 }}>
    <label className="f-label up">{label}</label>
    <div>{value}</div>
  </div>
);

// Visual timeline of the transfer's chain of custody.
const Timeline = ({ t }) => {
  const steps = [
    { key: 'requested',  label: 'Requested',  who: t.requested_by_name,   at: t.created_at,      done: true },
    { key: 'approved',   label: 'Approved',   who: t.approved_by_name,    at: t.approved_at,     done: !!t.approved_at },
    { key: 'dispatched', label: 'Dispatched', who: t.dispatched_by_name,  at: t.dispatched_at,   done: !!t.dispatched_at },
    { key: 'received',   label: 'Received',   who: t.received_by_name,    at: t.received_at,     done: !!t.received_at,
      tone: t.has_variance ? 'warn' : 'ok' },
  ];
  if (t.status === 'rejected') {
    steps.push({ key: 'rejected', label: 'Rejected', who: t.rejected_by_name, at: t.rejected_at, done: true, tone: 'danger' });
  }
  if (t.status === 'cancelled') {
    return (
      <div className="notice">
        <strong>Cancelled</strong> by {t.cancelled_by_name || 'system'} on {fmtDate(t.cancelled_at)}
      </div>
    );
  }
  return (
    <div className="tl">
      {steps.map((s, i) => (
        <div key={s.key} className={`tl-row ${s.done ? 'done' : ''} ${s.tone || ''}`}>
          <div className="tl-dot">{s.done ? '✓' : i + 1}</div>
          {i < steps.length - 1 && <div className="tl-bar" />}
          <div className="tl-text">
            <div className="tl-label">{s.label}</div>
            <div className="tl-meta">
              {s.done
                ? <>{s.who || '—'} · {fmtDate(s.at)}</>
                : <span style={{ color: 'var(--mute-2)' }}>pending</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Transfers;
